import EditableDatePicker from '@/shared/components/editable/EditableDatePicker';
import EditableNumber from '@/shared/components/editable/EditableNumber';
import EditableTextbox from '@/shared/components/editable/EditableTextbox';
import FormattedListValue, { listItemMsg } from '@/shared/components/FormattedListValue';
import EditableUserSearch from '@/shared/components/editable/EditableUserSearch';
import HasFeature from '@/shared/components/security/HasFeature';
import EditableTextArea from '@/shared/components/editable/EditableTextArea';

import classNames from 'classnames';
import {
  FaTrashAlt,
  FaShareAlt,
  FaRegClock,
  FaExternalLinkAlt,
  FaRegWindowRestore,
  FaPaperPlane,
} from 'react-icons/fa';

import intlShape from '@/shared/utils/intlShape';
import Checkbox from '@/shared/components/input/Checkbox';
import RelationName from '@/shared/components/RelationName';
import CanDelete from '@/shared/components/security/CanDelete';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedNumber } from 'react-intl';
import { CanUpdate } from '@/shared/components/security';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import { mapInvoiceStreamName } from '@/shared/services/ObjectDisplayService';
import { injectStateService, StateService } from '@/shared/services/StateService';
import EditableSelect from '@/shared/components/editable/EditableSelect';
import FormattedDateShort from '@/shared/components/FormattedDateShort';
import DataCacheService from '@/shared/services/DataCacheService';
import TaskStatusToggle from '../TaskStatusToggle';
import TaskOverviewCell from './TaskOverviewCell';
import EditableEnumSelectV2 from '@/shared/components/editable/EditableEnumSelectV2';

import messages from '../taskMessages';
import TaskRequiredAuthorizationsIcon from '../TaskRequiredAuthorizationsIcon';

class TaskOverviewRow extends React.PureComponent {
  renderSingleParameter = (parameterValue, selectionItems) => {
    const { onUpdateParameter } = this.props;
    const selectionItemsArray = selectionItems
      ? selectionItems.split(';').map((x, index) => ({
          id: index,
          label: x,
        }))
      : [];

    const selectedItem =
      parameterValue.parameterValueType === 4 &&
      selectionItemsArray.map(x => x.label).includes(parameterValue.valueText)
        ? selectionItemsArray.find(x => x.label === parameterValue.valueText).id
        : undefined;

    const selectedItemChanged = id => {
      // selection cleared
      if (!id) {
        onUpdateParameter({ ...parameterValue, valueText: null });
        return;
      }

      const item = selectionItemsArray.find(x => x.id === parseInt(id));
      if (!item) return;
      onUpdateParameter({ ...parameterValue, valueText: item.label });
    };

    if (parameterValue.systemParameterType === 26) {
      return (
        <EditableEnumSelectV2
          enumType="PitTaxReturnType"
          value={parameterValue.valueNumeric}
          onChange={value => onUpdateParameter({ ...parameterValue, valueNumeric: value })}
          allowNull
        />
      );
    }

    const highlight = parameterValue.computedParameterModifiedByUser ? 'red' : undefined;

    switch (parameterValue.parameterValueType) {
      case 0: // text
        return (
          <EditableTextbox
            displayClassName={highlight}
            value={parameterValue.valueText}
            onChange={value => onUpdateParameter({ ...parameterValue, valueText: value })}
            maxLength="500"
          />
        );
      case 1: // numeric
        return (
          <EditableNumber
            displayClassName={highlight}
            value={parameterValue.valueNumeric}
            onChange={value => onUpdateParameter({ ...parameterValue, valueNumeric: value })}
            max="99999999"
            min="-99999999"
          />
        );
      case 2: // Date
        return (
          <EditableDatePicker
            displayClassName={highlight}
            value={parameterValue.valueDate}
            onChange={value => onUpdateParameter({ ...parameterValue, valueDate: value })}
          />
        );
      case 3: // boolean
        return (
          <div
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <Checkbox
              extraClass={highlight}
              onClick={value => onUpdateParameter({ ...parameterValue, valueBoolean: value })}
              value={parameterValue.valueBoolean}
              renderNullAsFalse
            />
          </div>
        );
      case 4: // select list
        return (
          <EditableSelect
            className={highlight}
            value={selectedItem}
            onChange={id => selectedItemChanged(id)}
            options={selectionItemsArray}
          />
        );
      case 5: // employee
        return (
          <EditableUserSearch
            className={highlight}
            onChange={value => onUpdateParameter({ ...parameterValue, valueText: value?.id })}
            value={parameterValue.valueText}
            withUnassigned
            submitOnChange
            includeInactiveUsers
          />
        );
      case 6: // textarea
        return (
          <EditableTextArea
            value={parameterValue.valueText}
            onChange={value => onUpdateParameter({ ...parameterValue, valueText: value })}
            maxLength="500"
            rows={10}
            displayValue={
              parameterValue.valueText ? (
                <div className="bold linebreak">{parameterValue.valueText}</div>
              ) : null
            }
            style={{ minHeight: '100px' }}
          />
        );
      default:
        return <span />;
    }
  };

  renderSoftware = (software, softwareType) => {
    const { intl } = this.props;
    if (!software) return '';
    const softwareArray = software
      .replace(/\s/g, '')
      .split(',')
      .filter(x => x !== '');

    return (softwareArray || [])
      .map(x => intl.formatMessage(listItemMsg(softwareType, x)))
      .join(', ');
  };

  renderRelationFieldAsEnum = (parameterValue, listType) =>
    parameterValue.valueNumeric !== null && (
      <FormattedListValue list={listType} id={parameterValue.valueNumeric} />
    );

  renderRelationField = (parameterValue, type) => {
    // Special types (list values, enums, guids ...)
    const { intl } = this.props;
    const enumValues = {
      8: 'accountingsoftware',
      14: 'vatPeriodicity',
      18: 'financialStatementScheme',
      23: 'socialSecurityFund',
      24: 'socialSecretariat',
      28: 'shareregister',
      33: 'receiveInvoiceSoftware',
      36: 'relationPeppolStatus',
      37: 'PeppolReadyStatus',
    };

    if (type in enumValues) return this.renderRelationFieldAsEnum(parameterValue, enumValues[type]);

    switch (type) {
      case 9:
      case 10:
      case 11:
      case 12:
      case 26:
      case 27: {
        const user = DataCacheService.getUserName(parameterValue.valueText);
        return <span>{user}</span>;
      }

      case 15:
        return (
          parameterValue.valueObject !== null && (
            <RelationName relation={parameterValue.valueObject} />
          )
        );
      case 22:
        return this.renderSoftware(parameterValue.valueText, 'otherSoftware');
      case 32:
        return this.renderSoftware(parameterValue.valueText, 'sendInvoiceSoftware');
      case 35:
        return parameterValue.valueNumeric === null ? (
          intl.formatMessage(messages.GeneralSetting)
        ) : (
          <FormattedListValue list="sendInvoiceStrategy" id={parameterValue.valueNumeric} />
        );
      default:
        break;
    }
    // Normal values
    switch (parameterValue.parameterValueType) {
      case 0: // text
        return parameterValue.valueText;
      case 1: // numeric
        return !parameterValue.valueNumeric && parameterValue.valueNumeric !== 0 ? null : (
          <FormattedNumber value={parameterValue.valueNumeric} />
        );
      case 2: // Date
        return parameterValue.valueDate && <FormattedDateShort value={parameterValue.valueDate} />;
      case 3: // boolean
        return (
          <div className="checkbox">
            <Checkbox
              value={parameterValue.valueBoolean}
              renderNullAsFalse
              readOnly
              disabled
              onClick={() => {}}
            />
          </div>
        );
      default:
        return <span />;
    }
  };

  renderTaskParameters = (task, parameterDefinitions) => {
    const temp = parameterDefinitions.map(definition => {
      const parameterValue =
        !task.parameterValues || !task.parameterValues.length
          ? null
          : task.parameterValues.find(x => x.parameterDefinitionId === definition.id);
      if (!parameterValue) return <td key={definition.id} />;
      if (definition.relationFieldType) {
        return (
          <td className="text-center text-nowrap" key={definition.id}>
            {this.renderRelationField(parameterValue, definition.relationFieldType)}
          </td>
        );
      }
      return (
        <td className="text-center" key={definition.id}>
          {this.renderSingleParameter(parameterValue, definition.selectionItems)}
        </td>
      );
    });
    return temp;
  };

  getRegObjectForCell = colId => {
    const { task } = this.props;
    const subTask = task.columns[colId];
    if (!subTask) return null;

    return {
      id: subTask.id,
      templateId: subTask.templateId,
      name: subTask.name,
      resourceId: subTask.resourceId || task.resourceId,
      mainTaskId: task.id,
      mainTaskName: task.name,
      mainTemplateId: task.templateId,
      relation: task.relation,
      invoiceStreamId: task.invoiceStreamId,
      assignmentTemplateId: task.assignmentTemplateId,
      year: task.year,
    };
  };

  render() {
    const {
      task,
      columns,
      stateService,
      displayMode,
      intl,
      handleTaskSelection,
      parameterDefinitions,
      onShowTaskDetails,
      onAddRegistrationClick,
      onTasksUpdated,
      onRequestDeleteTask,
      onShareItem,
      onUpdateDeadline,
      onSendSignatureRequest,
      handleRowHighlight,
      requiredAuthorizations,
      isSelected,
      isHighlighted,
    } = this.props;
    const {
      id,
      relation,
      columns: cells,
      status,
      isBlocked,
      inapplicable,
      assignmentId,
      assignmentTemplateId,
      assignmentName,
      assignmentTemplateName,
      hasRemark,
      scheduleHasRemark,
      hasAttachments,
      rejected,
      remark,
      scheduleRemark,
      changedCounter,
    } = task;
    const statusValue = {
      id,
      status,
      isBlocked,
      inapplicable,
      rejected,
      hasAttachments,
      hasRemark,
      scheduleHasRemark,
      taskLevel: 0,
      changedCounter: changedCounter ?? 0,
    };
    const showLinkButton =
      task.status !== 2 || task.deadline === null || moment(task.deadline) > moment.utc();

    const showAssignments = hasAssignments();

    const canSendSignatureRequest =
      DataCacheService.getCompanySettings(task.relation.companyId) &&
      DataCacheService.getCompanySettings(task.relation.companyId).documentSigningProvider > 0;

    return (
      <tbody className="hover">
        <tr
          onClick={() => handleRowHighlight(task)}
          className={isHighlighted || isSelected ? 'highlight-row' : null}
        >
          <td className="sticky-column">
            <Checkbox
              onClick={(value, e) => {
                e.stopPropagation();
                handleTaskSelection(task);
              }}
              value={!!isSelected}
            />
          </td>
          <td className="sticky-column">
            <RelationName relation={relation} />
          </td>
          {requiredAuthorizations?.length > 0 ? (
            <td className="text-center no-wrap">
              <TaskRequiredAuthorizationsIcon
                taskId={task.id}
                relationId={relation.id}
                requiredAuthorizations={task.requiredAuthorizations}
              />
            </td>
          ) : null}
          {showAssignments && (
            <td>
              {mapInvoiceStreamName(
                { assignmentId, assignmentTemplateId, assignmentName, assignmentTemplateName },
                intl,
                '',
              )}
            </td>
          )}
          <td className="text-center no-wrap">
            <TaskStatusToggle
              id={`TaskStatusToggle-${task.id}`}
              value={statusValue}
              onTasksUpdated={onTasksUpdated}
              isMainTask
              title={intl.formatMessage(messages.ChangeMainTaskStatus)}
              onOpenDetail={() => onShowTaskDetails(task.id)}
              onAddRegistration={() => onAddRegistrationClick(task)}
              extraInfo={
                displayMode === 'dateDone' &&
                task.status === 2 &&
                task.completedDate &&
                !task.isBlocked ? (
                  <span className="margin5L">
                    <FormattedDateShort value={task.completedDate} />
                  </span>
                ) : null
              }
            />
          </td>
          <HasFeature feature="referenceYear">
            <td className="no-wrap">{task.year === 0 ? null : task.year}</td>
          </HasFeature>
          <td className="no-wrap">
            <EditableDatePicker
              showClearDate
              value={typeof task.deadline === 'string' ? moment.utc(task.deadline) : task.deadline}
              onChange={deadline => onUpdateDeadline({ ...task, deadline })}
            />
          </td>

          {parameterDefinitions && this.renderTaskParameters(task, parameterDefinitions)}

          {columns.map(col => (
            <TaskOverviewCell
              key={col.id}
              taskId={task.id}
              column={col}
              value={cells && cells[col.id]}
              assignedUser={
                cells && cells[col.id]
                  ? DataCacheService.getUserName(cells[col.id].userId || task.userId)
                  : null
              }
              displayMode={displayMode}
              intl={intl}
              onTasksUpdated={onTasksUpdated}
              onOpenDetail={onShowTaskDetails}
              onAddRegistration={() => onAddRegistrationClick(this.getRegObjectForCell(col.id))}
            />
          ))}
          <td className="text-right actions-column">
            <a
              className="btn btn-xs btn-icon"
              title={intl.formatMessage(messages.AddRegistration)}
              onClick={e => {
                onAddRegistrationClick(task);
                e.stopPropagation();
              }}
            >
              <FaRegClock />
            </a>
            {canSendSignatureRequest && (
              <CanUpdate on="Relation">
                <a
                  className="btn btn-xs btn-icon"
                  title={intl.formatMessage(messages.SendDocumentsLinkedToTaskOutForSignature)}
                  onClick={e => {
                    onSendSignatureRequest(task);
                    e.stopPropagation();
                  }}
                >
                  <FaPaperPlane />
                </a>
              </CanUpdate>
            )}
            <a
              className="btn btn-xs btn-icon"
              title={intl.formatMessage(messages.OpenTaskDetails)}
              onClick={e => {
                onShowTaskDetails(task.id);
                e.stopPropagation();
              }}
            >
              <FaRegWindowRestore />
            </a>
            <a
              className="btn btn-xs btn-icon"
              title={intl.formatMessage(messages.Share)}
              onClick={e => {
                onShareItem(task);
                e.stopPropagation();
              }}
            >
              <FaShareAlt />
            </a>
            <a
              className={classNames('btn btn-xs btn-icon', {
                hide: !showLinkButton,
              })}
              onClick={e => {
                stateService.gotoTaskboard({
                  relation,
                  taskTemplateId: task.templateId,
                  plannedTaskId: task.id,
                  openPlannedTaskDetailModal: false,
                });
                e.stopPropagation();
              }}
              title={intl.formatMessage(messages.OpenInTaskboard)}
            >
              <FaExternalLinkAlt />
            </a>
            <CanDelete on="PlannedTask">
              <a
                className="btn btn-xs btn-icon"
                title={intl.formatMessage(messages.Delete)}
                onClick={e => {
                  onRequestDeleteTask(task);
                  e.stopPropagation();
                }}
              >
                <FaTrashAlt />
              </a>
            </CanDelete>
          </td>
        </tr>
      </tbody>
    );
  }
}

TaskOverviewRow.propTypes = {
  task: PropTypes.shape({
    relation: PropTypes.shape({}),
    columns: PropTypes.shape({}),
    id: PropTypes.string,
    templateId: PropTypes.string,
    userId: PropTypes.string,
  }).isRequired,
  requiredAuthorizations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
  displayMode: PropTypes.oneOf(['status', 'dateDone', 'deadline', 'executionDate']).isRequired,
  intl: intlShape.isRequired,
  handleTaskSelection: PropTypes.func.isRequired,
  handleRowHighlight: PropTypes.func.isRequired,
  onShowTaskDetails: PropTypes.func.isRequired,
  onAddRegistrationClick: PropTypes.func.isRequired,
  onRequestDeleteTask: PropTypes.func.isRequired,
  onShareItem: PropTypes.func.isRequired,
  isSelected: PropTypes.bool,
  isHighlighted: PropTypes.bool,

  // onStatusChanged: PropTypes.func.isRequired,
  parameterDefinitions: PropTypes.arrayOf(PropTypes.shape({})),

  onTasksUpdated: PropTypes.func.isRequired,
  onUpdateParameter: PropTypes.func.isRequired,
};
TaskOverviewRow.defaultProps = {
  parameterDefinitions: [],
  displayMode: 'status',
  isSelected: false,
  isHighlighted: false,
};
export default injectStateService(TaskOverviewRow);
